/**
 * Per-run lead counts for the dashboard. Pure (no Firebase), so it can be tested on its own (tests/leadRunCounts.test.ts).
 *
 * Counts per run:
 *   - pending       : status='pending', regardless of recall window
 *                     (kept so callers like DeleteLeadsModal see the full
 *                     deletable set)
 *   - eligibleNow   : status='pending' AND (nextEligibleCallAt is null OR <= now)
 *                     This is what the dispatcher actually picks up — the chip
 *                     on the Run button shows this so the user doesn't see
 *                     "500 available" then click and get "No eligible leads".
 *   - waiting       : status='pending' AND nextEligibleCallAt > now
 *                     Recall delay still active. Surfaced as a tooltip / sub-line.
 *   - earliestEligibleMs : the soonest waiting lead's unlock time (null if none).
 */

export interface RunCount {
  pending: number;
  eligibleNow: number;
  waiting: number;
  /** Earliest waiting-lead unlock time as ms epoch. null when waiting === 0. */
  earliestEligibleMs: number | null;
}

export interface RunCounts {
  [key: number]: RunCount;
}

export const RUN_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8];

export function emptyRunCounts(): RunCounts {
  const counts: RunCounts = {};
  for (const run of RUN_NUMBERS) {
    counts[run] = { pending: 0, eligibleNow: 0, waiting: 0, earliestEligibleMs: null };
  }
  return counts;
}

/** The two counts are separate queries, so a lead can change between them: eligibleNow never goes below 0. */
export function runCountFrom(pending: number, waiting: number, earliestEligibleMs: number | null): RunCount {
  return {
    pending,
    eligibleNow: Math.max(0, pending - waiting),
    waiting,
    earliestEligibleMs: waiting > 0 ? earliestEligibleMs : null,
  };
}

/** Firestore's Timestamp (or a stored number) as ms epoch; anything else is null. */
export function nextEligibleToMs(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    return (value as { toMillis(): number }).toMillis();
  }
  return null;
}

/** A send or delete just finished: refresh the counts now instead of at the next timed refresh. */
export const LEADS_CHANGED_EVENT = 'jad:leads-changed';

export function notifyLeadsChanged(): void {
  window.dispatchEvent(new Event(LEADS_CHANGED_EVENT));
}
